/**
 * Diccionario › Faltantes (encargo 49): el registro de lo que el diccionario no
 * tenía cuando un alumno tocó una palabra. Pedido de Emmanuel (06/10): «control
 * claro de las palabras que va generando el modelo, con timestamps claros de
 * cuándo se crearon», sin marcas ni palabras inapropiadas.
 *
 * La lista dice cuándo se registró cada candidato (UTC y hora de Argentina),
 * su estado y su motivo; el detalle, el historial completo de cambios con quién
 * los hizo. Nunca se muestra qué alumno lo tocó: sólo cuántas veces.
 */
import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  actOnMissingCandidate,
  fetchMissingCandidate,
  fetchMissingCandidates,
  syncPublishedMissing,
  MISSING_STATUSES,
  type FilterStep,
  type MissingCandidate,
  type MissingFilters,
  type MissingStatus,
} from '../../api/admin/lexicon-missing.js';
import { useUrlParam } from '../../lib/useUrlTab.js';

export const STATUS_LABEL: Record<MissingStatus, string> = {
  pending: 'pendiente',
  needs_human: 'revisión humana',
  rejected: 'rechazado',
  in_review: 'en revisión',
  judge_approved: 'aprobado por el juez',
  approved: 'aprobado',
  exported: 'exportado',
  published: 'publicado',
  not_included: 'no entró',
};

const STATUS_BADGE: Record<MissingStatus, string> = {
  pending: 'badge b-info',
  needs_human: 'badge b-warn',
  rejected: 'badge',
  in_review: 'badge b-info',
  judge_approved: 'badge b-success',
  approved: 'badge b-success',
  exported: 'badge b-success',
  published: 'badge b-success',
  not_included: 'badge',
};

const FILTER_LABEL: Record<string, string> = {
  no_en_diccionario: 'No está en el diccionario',
  es_palabra: 'Es una palabra',
  idioma: 'Está en inglés',
  impropia: 'No es impropia (decisión 59)',
  marca: 'No es una marca, app o producto',
  nombre_propio: 'No es nombre propio ni sigla',
  palabra_conocida: 'Es una palabra real (wordfreq, familiaridad)',
  typo: 'No es un error de tipeo',
  apta: 'Contexto apto (Jev)',
  persona: 'Una persona lo destrabó',
};

const RESULT_BADGE: Record<FilterStep['resultado'], string> = {
  pasa: 'badge b-success',
  frena: 'badge b-danger',
  dudoso: 'badge b-warn',
  no_aplica: 'badge',
};

/** El recorrido de filtros: cuál pasó, cuál lo frenó y cuál dejó dudas. */
function FiltersTable({ steps }: { steps: FilterStep[] }): ReactElement {
  return (
    <table
      className="data-table"
      data-testid="missing-filters-trail"
      style={{ width: '100%', borderCollapse: 'collapse' }}
    >
      <thead>
        <tr>
          <th style={{ width: 300 }}>Filtro</th>
          <th style={{ width: 110 }}>Resultado</th>
          <th>Detalle</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((f, i) => (
          <tr key={i}>
            <td>{FILTER_LABEL[f.filtro] ?? f.filtro}</td>
            <td>
              <span className={RESULT_BADGE[f.resultado]}>{f.resultado.replace('_', ' ')}</span>
            </td>
            <td className="t-meta">{f.detalle}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const REASON_LABEL: Record<string, string> = {
  not_a_word: 'no es una palabra',
  not_english: 'no es inglés',
  proper_name: 'nombre propio o sigla',
  brand: 'marca',
  improper: 'impropia',
  possible_typo: 'posible error de tipeo',
  apta_doubtful: '«apta» dudosa',
  apta_no_answer: '«apta» sin respuesta',
  human: 'decisión de una persona',
  unknown_word: 'no está en las listas de referencia',
  writer_doubt: 'el escritor dudó',
  writer_invalid: 'el escritor no respondió bien',
  judge_invalid: 'el juez no respondió bien',
  judge_rejected: 'el juez no lo aprobó',
  example_unsuitable: 'ejemplo no apto',
  writer_cut: 'escritura cortada',
  writer_error: 'error al escribir',
  budget: 'sin saldo',
};

const ORIGIN_LABEL: Record<MissingCandidate['origin'], string> = {
  coach: 'charla (coach)',
  learner: 'charla (alumno)',
  definition: 'ficha · definición',
  example: 'ficha · ejemplo',
  'example-es': 'ficha · traducción del ejemplo',
};

const AR = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** «2026-10-06 18:02:05 UTC» y «06/10/2026 15:02:05 en Argentina»: las dos, siempre. */
export function bothTimes(iso: string): { utc: string; ar: string } {
  const d = new Date(iso);
  return {
    utc: `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`,
    ar: `${AR.format(d)} (Argentina)`,
  };
}

function When({ iso }: { iso: string }): ReactElement {
  const t = bothTimes(iso);
  return (
    <span
      className="mono"
      style={{ display: 'inline-flex', flexDirection: 'column', fontSize: 12 }}
    >
      <span>{t.utc}</span>
      <span className="t-meta">{t.ar}</span>
    </span>
  );
}

function Detail({ id, onClose }: { id: string; onClose: () => void }): ReactElement {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'lexicon-missing', id],
    queryFn: () => fetchMissingCandidate(id),
  });
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: (a: { action: 'reject' | 'reopen' | 'approve'; reason?: string }) =>
      actOnMissingCandidate(id, a.action, a.reason),
    onSuccess: () => {
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'lexicon-missing'] });
    },
  });
  if (q.isPending) return <div className="loading">Cargando…</div>;
  if (q.isError) return <div className="loading">Error: {q.error.message}</div>;
  const { candidate: c, events } = q.data;
  const canReject = ['pending', 'needs_human', 'in_review', 'judge_approved', 'approved'].includes(
    c.status,
  );
  const canReopen = ['rejected', 'needs_human'].includes(c.status);
  const canApprove = c.status === 'judge_approved';
  return (
    <section className="card" data-testid="missing-detail" style={{ marginTop: 12, padding: 16 }}>
      <div
        className="flex gap-2"
        style={{ justifyContent: 'space-between', alignItems: 'baseline' }}
      >
        <h2 className="page-title" style={{ fontSize: 18 }}>
          «{c.word}» <span className={STATUS_BADGE[c.status]}>{STATUS_LABEL[c.status]}</span>
        </h2>
        <button type="button" className="chip" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <p className="t-meta" style={{ margin: '6px 0' }}>
        {c.kind === 'word' ? 'Falta la palabra' : 'Falta un significado'} · tocada como «{c.surface}
        » en {ORIGIN_LABEL[c.origin]} · {c.hits} {c.hits === 1 ? 'toque' : 'toques'}
      </p>
      <blockquote className="mono" style={{ margin: '8px 0', fontSize: 13 }}>
        {c.sentence}
      </blockquote>
      <p className="t-meta">
        {c.students === 1 ? '1 alumno' : `${c.students} alumnos distintos`} · origen{' '}
        {c.originRef ?? '—'} · Selector: {c.selector ?? '—'} · Escritor: {c.writerModel ?? '—'} ·
        Juez: {c.judgeModel ?? '—'} · Último toque: {bothTimes(c.lastSeenAt).utc}
      </p>

      <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>Filtros</h3>
      <FiltersTable steps={c.filters} />

      {c.draft && (
        <>
          <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>
            Significado provisorio{c.kind === 'word' ? ' (lo que vería el alumno tras tu OK)' : ''}
          </h3>
          <div className="card" data-testid="missing-draft" style={{ padding: 12 }}>
            <div>
              <b>{c.draft.translation}</b>
              {c.draft.clarification && (
                <span className="t-meta"> ({c.draft.clarification})</span>
              )}{' '}
              <span className="badge">{c.draft.pos}</span>
            </div>
            <div className="t-meta" style={{ marginTop: 4 }}>
              {c.draft.definition}
            </div>
            <div style={{ marginTop: 6 }}>{c.draft.example}</div>
            <div className="t-meta">{c.draft.exampleEs}</div>
          </div>
        </>
      )}
      {c.review && c.review.rounds.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>Juez, ronda por ronda</h3>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {c.review.rounds.map((r, i) => (
              <li key={i} className="t-meta">
                Ronda {i + 1}:{' '}
                {r.verdict
                  ? r.verdict.approved
                    ? 'aprobado'
                    : `no aprobado — ${r.verdict.problems.join(' · ')}`
                  : 'sin veredicto'}
                {r.writer && ` · escritor ${r.writer.inputTokens}+${r.writer.outputTokens} tokens`}
                {r.judge && ` · juez ${r.judge.inputTokens}+${r.judge.outputTokens} tokens`}
              </li>
            ))}
          </ul>
        </>
      )}
      {c.kind === 'word' &&
        (c.status === 'approved' || c.status === 'published' || c.status === 'not_included') && (
          <p className="t-meta" style={{ marginTop: 10 }}>
            Aprobada por {c.approvedBy ?? '—'}
            {c.approvedAt ? ` el ${bothTimes(c.approvedAt).utc}` : ''}.{' '}
            {c.status === 'published'
              ? `Ya está en el diccionario (${c.publishedAt ? bothTimes(c.publishedAt).utc : ''}).`
              : c.status === 'not_included'
                ? 'No entró: el motivo está en el informe de la pasada mensual.'
                : 'Espera la pasada de la #409 (parte «nuevas»), que la lee de la vista lexicon_new_words_approved.'}
          </p>
        )}

      <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>Historial</h3>
      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ width: 210 }}>Cuándo</th>
            <th style={{ width: 230 }}>Cambio</th>
            <th>Quién y por qué</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <td>
                <When iso={e.at} />
              </td>
              <td>
                {e.from ? `${STATUS_LABEL[e.from]} → ` : 'registrado → '}
                <span className={STATUS_BADGE[e.to]}>{STATUS_LABEL[e.to]}</span>
              </td>
              <td>
                <div>{e.actor}</div>
                {e.reason && <div className="t-meta">{e.reason}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex gap-2" style={{ marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {canReject && (
          <>
            <input
              className="input"
              placeholder="Motivo del rechazo (obligatorio)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ width: 280 }}
            />
            <button
              type="button"
              className="btn"
              disabled={!reason.trim() || act.isPending}
              onClick={() => act.mutate({ action: 'reject', reason: reason.trim() })}
            >
              Rechazar
            </button>
          </>
        )}
        {canReopen && (
          <button
            type="button"
            className="btn"
            disabled={act.isPending}
            onClick={() => act.mutate({ action: 'reopen' })}
          >
            Volver a pendiente
          </button>
        )}
        {canApprove && (
          <button
            type="button"
            className="btn"
            disabled={act.isPending}
            onClick={() => act.mutate({ action: 'approve' })}
          >
            Aprobar para exportar
          </button>
        )}
        {!canApprove && (
          <span className="t-meta">
            Aprobar se habilita cuando el juez lo aprobó
            {c.kind === 'meaning' ? ' (cola de revisión de la #457)' : ''}.
          </span>
        )}
        {act.isError && <span className="badge b-danger">{act.error.message}</span>}
      </div>
    </section>
  );
}

export function DictionaryMissingPage(): ReactElement {
  const [status, setStatus] = useState<MissingStatus | 'all'>('all');
  const [kind, setKind] = useState<'all' | 'meaning' | 'word'>('all');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openId, setOpenId] = useUrlParam('id');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const filters: MissingFilters = {
    ...(status !== 'all' ? { status } : {}),
    ...(kind !== 'all' ? { kind } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(from ? { from: new Date(`${from}T00:00:00Z`).toISOString() } : {}),
    ...(to ? { to: new Date(`${to}T23:59:59.999Z`).toISOString() } : {}),
    ...(cursor ? { cursor } : {}),
  };
  const query = useQuery({
    queryKey: ['admin', 'lexicon-missing', 'list', filters],
    queryFn: () => fetchMissingCandidates(filters),
    refetchInterval: 30_000,
  });
  const resetPage = () => setCursors([]);
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: syncPublishedMissing,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'lexicon-missing'] }),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Diccionario · Faltantes</h1>
          <p className="page-sub">
            Lo que el diccionario no tenía cuando un alumno tocó una palabra. Nada de esto se
            muestra ni se publica sin revisión: lo aprobado sale por el circuito mensual del
            catálogo. Horas en UTC y en Argentina (UTC−3).
          </p>
        </div>
        <button
          type="button"
          className="btn"
          disabled={sync.isPending}
          onClick={() => sync.mutate()}
        >
          {sync.data
            ? `Verificar publicación (${sync.data.published} publicadas, ${sync.data.notIncluded} no entraron)`
            : 'Verificar publicación'}
        </button>
      </div>

      <section className="sess-filters" data-testid="missing-filters">
        <span className="label">Estado</span>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {(['all', ...MISSING_STATUSES] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${status === s ? ' is-on' : ''}`}
              onClick={() => {
                setStatus(s);
                resetPage();
              }}
            >
              {s === 'all' ? 'todos' : STATUS_LABEL[s]}
              {s !== 'all' && query.data?.counts[s] ? ` · ${query.data.counts[s]}` : ''}
            </button>
          ))}
        </div>
        <span className="label" style={{ marginLeft: 16 }}>
          Tipo
        </span>
        <div className="flex gap-2">
          {(['all', 'meaning', 'word'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className={`chip${kind === k ? ' is-on' : ''}`}
              onClick={() => {
                setKind(k);
                resetPage();
              }}
            >
              {k === 'all' ? 'todos' : k === 'meaning' ? 'significado' : 'palabra'}
            </button>
          ))}
        </div>
        <input
          className="input"
          placeholder="palabra…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            resetPage();
          }}
          style={{ width: 140, marginLeft: 16 }}
        />
        <span className="label" style={{ marginLeft: 16 }}>
          Desde
        </span>
        <input
          className="input"
          type="date"
          value={from}
          onChange={(e) => (setFrom(e.target.value), resetPage())}
        />
        <span className="label">Hasta</span>
        <input
          className="input"
          type="date"
          value={to}
          onChange={(e) => (setTo(e.target.value), resetPage())}
        />
      </section>

      {openId && <Detail id={openId} onClose={() => setOpenId(null)} />}

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <section className="card" style={{ padding: 0, marginTop: 12 }}>
          {query.data.candidates.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              No hay candidatos con estos filtros.
            </div>
          ) : (
            <table
              className="data-table"
              data-testid="missing-table"
              style={{ width: '100%', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  <th style={{ width: 210 }}>Registrado</th>
                  <th style={{ width: 140 }}>Palabra</th>
                  <th style={{ width: 150 }}>Estado</th>
                  <th>Motivo</th>
                  <th style={{ width: 160 }}>Origen</th>
                  <th style={{ width: 70 }}>Toques</th>
                  <th style={{ width: 70 }}>Alumnos</th>
                </tr>
              </thead>
              <tbody>
                {query.data.candidates.map((c) => (
                  <tr key={c.id} className={openId === c.id ? 'is-selected' : undefined}>
                    <td>
                      <When iso={c.createdAt} />
                    </td>
                    <td>
                      <button type="button" className="link-btn" onClick={() => setOpenId(c.id)}>
                        {c.word}
                      </button>
                      <div className="t-meta">
                        {c.kind === 'word' ? 'palabra nueva' : 'significado'}
                      </div>
                    </td>
                    <td>
                      <span className={STATUS_BADGE[c.status]}>{STATUS_LABEL[c.status]}</span>
                    </td>
                    <td>
                      {c.reasonCode && <div>{REASON_LABEL[c.reasonCode] ?? c.reasonCode}</div>}
                      {c.reason && <div className="t-meta">{c.reason}</div>}
                    </td>
                    <td>{ORIGIN_LABEL[c.origin]}</td>
                    <td className="mono">{c.hits}</td>
                    <td className="mono">{c.students}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex gap-2" style={{ padding: 12 }}>
            {cursors.length > 0 && (
              <button
                type="button"
                className="chip"
                onClick={() => setCursors((s) => s.slice(0, -1))}
              >
                ← Anteriores
              </button>
            )}
            {query.data.nextCursor && (
              <button
                type="button"
                className="chip"
                onClick={() => setCursors((s) => [...s, query.data.nextCursor!])}
              >
                Siguientes →
              </button>
            )}
          </div>
        </section>
      )}
    </>
  );
}
